import { Link } from "react-router";
import { StartKarte } from "../Startleitfaden";
import { useQuery } from "@tanstack/react-query";
import type { StaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { ErrorState, Loading } from "../../../components/States";
import { KachelBetrag, PageHead } from "../../../components/ui";
import { date, money, percent, today as todayIn } from "../../../lib/format";
import { t } from "../../../lib/sprache";
import { smallBusinessLabel } from "../einstellungen/model";
import { statusLabel } from "./labels";
import { kleinunternehmerStand, studioZahlen, STUDIO_RECHNUNG_SPALTEN, type StudioRechnung } from "./studio-model";
import { NurSchreiben } from "../../../app/nurLesen";

/**
 * Übersicht im Creator-Konto (0178): eigene Zahlen statt Agenturanteil und Creator-Leistung. Umsatz im Jahr, offene und
 * überfällige Rechnungen an Marken, die Kleinunternehmergrenze (wenn gewählt) und die laufenden Deals mit ihrer nächsten Frist.
 */
interface DealZeile {
  id: string;
  title: string;
  status: string;
  content_deadline: string | null;
  brand_id: string;
  brand?: { name: string } | null;
}

async function ladeStudio(orgId: string) {
  const [rechnungen, deals, settings, marken] = await Promise.all([
    api.select<StudioRechnung>("invoices", {
      columns: STUDIO_RECHNUNG_SPALTEN,
      filters: [["org_id", "eq", orgId], ["kind", "eq", "agency_to_brand"]],
    }),
    api.select<DealZeile>("deals", {
      columns: "id, title, status, content_deadline, brand_id",
      filters: [["org_id", "eq", orgId], ["status", "in", ["planning", "contract", "concept", "production", "content_approval", "published", "reporting", "invoicing", "blocked"]], ["archived_at", "is", null]],
      order: [["content_deadline", "asc"]],
    }),
    api.select<{ small_business: boolean; country: string | null; currency: string }>("org_settings", {
      columns: "small_business, country, currency", filters: [["org_id", "eq", orgId]],
    }),
    api.select<{ id: string; name: string }>("brands", { columns: "id, name", filters: [["org_id", "eq", orgId]] }),
  ]);
  const name = new Map(marken.map((b) => [b.id, b.name]));
  return { rechnungen, deals: deals.map((d) => ({ ...d, brand: name.has(d.brand_id) ? { name: name.get(d.brand_id)! } : null })), settings: settings[0] ?? null };
}

export function StudioUebersicht({ ws }: { ws: StaffWorkspace }) {
  const orgId = ws.org.org_id;
  const q = useQuery({ queryKey: ["studio_uebersicht", orgId], queryFn: () => ladeStudio(orgId) });
  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (q.isPending) return <Loading />;
  const heute = todayIn(ws.org.timezone);
  const waehrung = q.data.settings?.currency ?? ws.org.currency;
  const z = studioZahlen(q.data.rechnungen, heute, waehrung);
  const m = (c: number) => money(c, waehrung);
  const klein = q.data.settings?.small_business ? kleinunternehmerStand(q.data.settings.country, waehrung, z) : null;
  const jahr = heute.slice(0, 4);
  return (
    <main id="main" className="content">
      <PageHead eyebrow={ws.org.name} title={t("Übersicht", "Overview")} text={t("Dein Umsatz, deine offenen Rechnungen und was als Nächstes fällig ist.", "Your revenue, your open invoices and what is due next.")}>
        <NurSchreiben><Link className="btn" to="/agentur/anfragen/neu">{t("Anfrage erfassen", "Add inquiry")}</Link></NurSchreiben>
        {/* Nur-Lesen (Test-Chat 04.11.2026): Anlegen entfällt ausdrücklich, nicht nur über die Regel in portal.css */}
        <NurSchreiben><Link className="btn" to="/agentur/kooperationen/neu">{t("Deal anlegen", "Create deal")}</Link></NurSchreiben>
        <NurSchreiben><Link className="btn primary" to="/agentur/rechnungen/neu">{t("Rechnung schreiben", "Write invoice")}</Link></NurSchreiben>
      </PageHead>

      <StartKarte ws={ws} />

      <section>
        <div className="overview-kpis">
          <div className="kpi">
            <small>{t(`Umsatz ${jahr} · netto`, `Revenue ${jahr} · net`)}</small>
            <KachelBetrag cents={z.umsatzJahrCents} currency={waehrung} />
            <small>{t("Festgeschriebene Rechnungen nach Rechnungsdatum, ohne Storno und Verzugskosten", "Finalised invoices by invoice date, excluding cancellations and late-payment costs")}</small>
          </div>
          <div className="kpi">
            <small>{t("Offen · brutto", "Open · gross")}</small>
            <KachelBetrag cents={z.offenCents} currency={waehrung} />
            <small>{z.offenAnzahl === 1 ? t("1 Rechnung wartet auf Zahlung", "1 invoice awaiting payment") : t(`${z.offenAnzahl} Rechnungen warten auf Zahlung`, `${z.offenAnzahl} invoices awaiting payment`)}</small>
          </div>
          <div className="kpi">
            <small>{t("Überfällig · brutto", "Overdue · gross")}</small>
            <KachelBetrag cents={z.ueberfaelligCents} currency={waehrung} />
            <small>{z.ueberfaelligAnzahl
              ? <Link to="/agentur/rechnungen?filter=ueberfaellig">{z.ueberfaelligAnzahl === 1 ? t("1 Rechnung erinnern", "Remind 1 invoice") : t(`${z.ueberfaelligAnzahl} Rechnungen erinnern`, `Remind ${z.ueberfaelligAnzahl} invoices`)}</Link>
              : t("Nichts überfällig", "Nothing overdue")}</small>
          </div>
          <div className="kpi">
            <small>{t(`Eingegangen ${jahr} · brutto`, `Received ${jahr} · gross`)}</small>
            <KachelBetrag cents={z.bezahltJahrCents} currency={waehrung} />
            <small>{t("Als bezahlt erfasste Rechnungen", "Invoices recorded as paid")}</small>
          </div>
        </div>
        {z.weitere.length > 0 && (
          // K-294: Rechnungen in anderen Währungen nicht still weglassen
          <p className="muted">
            {t("In anderen Währungen (nicht in den Zahlen oben): ", "In other currencies (not included above): ")}
            {z.weitere.map((w) => t(
              `${money(w.umsatzJahrCents, w.waehrung)} Umsatz ${jahr}${w.bezahltJahrCents ? `, ${money(w.bezahltJahrCents, w.waehrung)} eingegangen` : ""}${w.offenCents ? `, ${money(w.offenCents, w.waehrung)} offen` : ""}${w.ueberfaelligCents ? `, davon ${money(w.ueberfaelligCents, w.waehrung)} überfällig` : ""}`,
              `${money(w.umsatzJahrCents, w.waehrung)} revenue ${jahr}${w.bezahltJahrCents ? `, ${money(w.bezahltJahrCents, w.waehrung)} received` : ""}${w.offenCents ? `, ${money(w.offenCents, w.waehrung)} open` : ""}${w.ueberfaelligCents ? `, of which ${money(w.ueberfaelligCents, w.waehrung)} overdue` : ""}`)).join(" · ")}
          </p>
        )}
      </section>

      {klein && (
        <section className="panel stack" aria-labelledby="klein-titel">
          <h2 id="klein-titel">{smallBusinessLabel(q.data.settings?.country)}</h2>
          <div className="progress" role="img" aria-label={t(`${percent(Math.round(klein.anteil * 100))} der Grenze`, `${percent(Math.round(klein.anteil * 100))} of the limit`)}>
            <span style={{ width: `${Math.min(100, Math.round(klein.anteil * 100))}%` }}></span>
          </div>
          <p>
            {klein.basis === "gross"
              ? t(`${m(klein.umsatzCents)} brutto von ${m(klein.grenzeCents)} brutto im Jahr ${jahr} (${klein.gesetz}); einmalige Toleranz bis ${m(klein.toleranzCents ?? klein.grenzeCents)}.`,
                `${m(klein.umsatzCents)} gross of ${m(klein.grenzeCents)} gross in ${jahr} (${klein.gesetz}); one-time tolerance up to ${m(klein.toleranzCents ?? klein.grenzeCents)}.`)
              : klein.land === "DE"
                ? t(`${m(klein.umsatzCents)} netto von ${m(klein.grenzeCents)} netto im laufenden Jahr ${jahr} (${klein.gesetz}). Vorjahr ${Number(jahr) - 1}: ${m(klein.vorjahrCents)} netto, Grenze ${m(klein.vorjahresgrenzeCents ?? 0)} netto.`,
                  `${m(klein.umsatzCents)} net of ${m(klein.grenzeCents)} net in the current year ${jahr} (${klein.gesetz}). Previous year ${Number(jahr) - 1}: ${m(klein.vorjahrCents)} net, limit ${m(klein.vorjahresgrenzeCents ?? 0)} net.`)
                : t(`${m(klein.umsatzCents)} netto im Jahr ${jahr}; MWST-pflichtig ab ${m(klein.grenzeCents)} (${klein.gesetz}).`,
                  `${m(klein.umsatzCents)} net in ${jahr}; liable for VAT from ${m(klein.grenzeCents)} (${klein.gesetz}).`)}
            {klein.stufe === "nah" && t(" Du näherst dich der Grenze: sprich rechtzeitig mit deiner Steuerberatung.", " You are approaching the limit: talk to your tax advisor in good time.")}
            {klein.stufe === "toleranz" && t(` Die Grenze ist überschritten, aber innerhalb der einmaligen Toleranz: dieses Jahr bleibst du befreit, ab 1. Januar ${Number(jahr) + 1} brauchst du Umsatzsteuer. Über ${m(klein.toleranzCents ?? 0)} endet die Befreiung sofort.`,
              ` The limit is exceeded but within the one-time tolerance: you remain exempt this year, from 1 January ${Number(jahr) + 1} you need VAT. Above ${m(klein.toleranzCents ?? 0)} the exemption ends immediately.`)}
            {klein.stufe === "drueber" && (klein.land === "CH" || klein.land === "LI"
              ? t(" Die Grenze ist erreicht: die MWST-Pflicht beginnt nach Ablauf des Geschäftsjahres, in dem sie erreicht wurde (bei Neugründung zählt die Prognose für die ersten zwölf Monate). Die Anmeldung bei der Steuerverwaltung muss innert 30 Tagen erfolgen; danach die MWST-Nummer unter Einstellungen · Firma eintragen.",
                " The limit is reached: VAT liability starts after the end of the business year in which it was reached (for a new business, the forecast for the first twelve months counts). Registration with the tax administration must follow within 30 days; then enter the VAT number under Settings · Company.")
              : t(" Die Grenze ist überschritten: ab jetzt brauchst du in der Regel Umsatzsteuer auf deinen Rechnungen. Bitte mit der Steuerberatung klären und unter Einstellungen · Firma umstellen.", " The limit is exceeded: from now on you usually need VAT on your invoices. Please clarify with your tax advisor and switch under Settings · Company."))}
            {klein.stufe === "nah" && (klein.land === "CH" || klein.land === "LI") && t(" Massgeblich ist der weltweite Umsatz, auch in Euro; bei Neugründung die Prognose für die ersten zwölf Monate.", " What counts is worldwide turnover, including euros; for a new business, the forecast for the first twelve months.")}
            {klein.stufe !== "drueber" && klein.folgejahrDrueber && t(` Dein Umsatz ${jahr} liegt über ${m(klein.vorjahresgrenzeCents ?? 0)} netto: dieses Jahr bleibst du Kleinunternehmer, ab 1. Januar ${Number(jahr) + 1} gilt die Regelbesteuerung.`,
              ` Your ${jahr} revenue exceeds ${m(klein.vorjahresgrenzeCents ?? 0)} net: you remain a small business this year, standard VAT applies from 1 January ${Number(jahr) + 1}.`)}
            {klein.vorjahrDrueber && t(" Der Umsatz im Vorjahr lag über der Vorjahresgrenze.", " Last year's revenue was above the previous-year limit.")}
          </p>
          {klein.auslandCents !== 0 && (
            <small>{t(`Nicht eingerechnet: ${m(klein.auslandCents)} ${klein.basis === "gross" ? "brutto" : "netto"} mit Leistungsort im Ausland (Reverse Charge, Drittland); sie zählen nicht zur Kleinunternehmergrenze.`,
              `Not included: ${m(klein.auslandCents)} ${klein.basis === "gross" ? "gross" : "net"} with place of supply abroad (reverse charge, third country); they do not count towards the small-business limit.`)}</small>
          )}
          {(z.weitere.length > 0 || z.ohneKurs > 0) && (
            <small>{z.ohneKurs > 0
              ? t(`Rechnungen in anderen Währungen sind zum Kurs beim Versand eingerechnet; ${z.ohneKurs === 1 ? "1 Rechnung ohne Kurs (noch nicht versendet) fehlt" : `${z.ohneKurs} Rechnungen ohne Kurs (noch nicht versendet) fehlen`} noch.`,
                `Invoices in other currencies are included at the rate when sent; ${z.ohneKurs === 1 ? "1 invoice without a rate (not yet sent) is" : `${z.ohneKurs} invoices without a rate (not yet sent) are`} still missing.`)
              : t("Rechnungen in anderen Währungen sind zum Kurs beim Versand eingerechnet.", "Invoices in other currencies are included at the rate when sent.")}</small>
          )}
          <small>{t("Nur Rechnungen aus CreatorOffice; Umsätze außerhalb zählen mit. Keine Steuerberatung.", "Only invoices from CreatorOffice; turnover outside also counts. Not tax advice.")}</small>
        </section>
      )}

      <section className="panel stack" aria-labelledby="deals-titel">
        <h2 id="deals-titel">{t("Laufende Deals", "Ongoing deals")}</h2>
        {q.data.deals.length === 0 ? (
          <p className="muted">{t("Gerade läuft kein Deal. Eine Anfrage einer Marke erfasst du unter „Anfragen“; aus der Zusage wird ein Deal.", "No deal is running right now. You capture a brand inquiry under “Inquiries”; a confirmed one becomes a deal.")}</p>
        ) : (
          <ul className="stack">
            {q.data.deals.map((d) => (
              <li key={d.id} className="task">
                <Link to={`/agentur/kooperationen/${d.id}`}>
                  <strong>{d.brand?.name ?? t("Marke", "Brand")}</strong> · {d.title}
                </Link>
                <small className="muted">
                  {statusLabel(d.status)}
                  {d.content_deadline ? t(` · Content bis ${date(d.content_deadline)}`, ` · content due ${date(d.content_deadline)}`) : ""}
                </small>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}
